import { Controller } from 'stimulus';

const ZOOM_LEVELS = {
  quarter: { dayWidth: 2 },
  year: { dayWidth: 0.5 },
};

const BAR_COLORS = [
  { bg: '#93C5FD', border: '#3B82F6', text: '#1E3A5F' },  // blue
  { bg: '#86EFAC', border: '#22C55E', text: '#14532D' },  // green
  { bg: '#FCA5A5', border: '#EF4444', text: '#7F1D1D' },  // red
  { bg: '#FDBA74', border: '#F97316', text: '#7C2D12' },  // orange
  { bg: '#C4B5FD', border: '#8B5CF6', text: '#3B0764' },  // purple
  { bg: '#FDE68A', border: '#EAB308', text: '#713F12' },  // yellow
];

const SIDEBAR_WIDTH = 192;
const HEADER_HEIGHT = 36;
const ROW_HEIGHT = 36;
const AISLE_HEADER_HEIGHT = 28;
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default class extends Controller {
  static targets = [
    'scrollContainer', 'gridContainer', 'zoomBtn',
    'unscheduledList', 'unscheduledChevron', 'unscheduledCount',
    'sidePanel', 'panelBackdrop', 'panelTitle', 'panelForm',
    'panelStatus', 'panelRequester', 'panelDept', 'panelStart', 'panelEnd',
    'panelBaysContainer', 'panelDescription', 'panelLink',
  ];

  connect() {
    this.payload = JSON.parse(this.data.get('payload'));
    this.currentZoom = this.data.get('zoom') || 'quarter';
    this.collapsedAisles = new Set();
    this.calculateDateRange();
    this.render();
    this._renderUnscheduled();
    this.scrollToToday();
  }

  // --- Date helpers ---

  calculateDateRange() {
    let minDate = null;
    let maxDate = null;

    (this.payload.aisles || []).forEach((aisle) => {
      aisle.bays.forEach((bay) => {
        bay.requests.forEach((req) => {
          if (req.confirmed_start_date) {
            const s = this._parseDate(req.confirmed_start_date);
            if (!minDate || s < minDate) minDate = s;
          }
          if (req.confirmed_end_date) {
            const e = this._parseDate(req.confirmed_end_date);
            if (!maxDate || e > maxDate) maxDate = e;
          }
        });
      });
    });

    const today = new Date();
    const startYear = minDate ? Math.min(minDate.getFullYear(), today.getFullYear() - 1) : today.getFullYear() - 1;
    const endYear = maxDate ? Math.max(maxDate.getFullYear() + 3, today.getFullYear() + 5) : today.getFullYear() + 5;

    this.startDate = new Date(startYear, 0, 1);
    this.endDate = new Date(endYear, 11, 31);
    this.totalDays = this._daysBetween(this.startDate, this.endDate);
  }

  _parseDate(str) {
    return new Date(str + 'T00:00:00');
  }

  _daysBetween(a, b) {
    return Math.round((b - a) / 86400000) + 1;
  }

  _dayOffset(date) {
    return Math.round((date - this.startDate) / 86400000);
  }

  get dayWidth() {
    return ZOOM_LEVELS[this.currentZoom].dayWidth;
  }

  get timelineWidth() {
    return this.totalDays * this.dayWidth;
  }

  // --- Rendering ---

  render() {
    const grid = this.gridContainerTarget;
    grid.innerHTML = '';
    grid.style.width = `${SIDEBAR_WIDTH + this.timelineWidth}px`;
    grid.style.position = 'relative';

    this._renderHeader(grid);
    this._renderBody(grid);
  }

  _renderHeader(grid) {
    const dw = this.dayWidth;

    // Header wrapper - sticky top
    const header = document.createElement('div');
    header.style.cssText = `position:sticky;top:0;z-index:20;display:flex;height:${HEADER_HEIGHT}px;background:#fff;`;

    // Corner cell - sticky left + top
    const corner = document.createElement('div');
    corner.style.cssText = `position:sticky;left:0;z-index:30;width:${SIDEBAR_WIDTH}px;min-width:${SIDEBAR_WIDTH}px;height:${HEADER_HEIGHT}px;background:#F9FAFB;border-bottom:1px solid #E5E7EB;border-right:1px solid #E5E7EB;display:flex;align-items:center;justify-content:center;`;
    corner.innerHTML = '<span style="font-size:11px;font-weight:600;color:#6B7280;text-transform:uppercase;letter-spacing:0.05em;">Resources</span>';
    header.appendChild(corner);

    // Timeline header area
    const headerArea = document.createElement('div');
    headerArea.style.cssText = `position:relative;width:${this.timelineWidth}px;height:${HEADER_HEIGHT}px;border-bottom:1px solid #E5E7EB;`;

    if (this.currentZoom === 'year') {
      this._renderYearHeader(headerArea);
    } else {
      this._renderQuarterHeader(headerArea);
    }

    header.appendChild(headerArea);
    grid.appendChild(header);
  }

  // Year zoom: single row of year labels
  _renderYearHeader(container) {
    const cursor = new Date(this.startDate);
    let currentYear = -1;
    let yearStartOffset = 0;

    while (cursor <= this.endDate) {
      const y = cursor.getFullYear();
      if (y !== currentYear) {
        if (currentYear !== -1) {
          this._addHeaderCell(container, yearStartOffset, this._dayOffset(cursor) - yearStartOffset, String(currentYear));
        }
        currentYear = y;
        yearStartOffset = this._dayOffset(cursor);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    if (currentYear !== -1) {
      this._addHeaderCell(container, yearStartOffset, this.totalDays - yearStartOffset, String(currentYear));
    }
  }

  // Quarter zoom: single row with "Q1 2025" labels
  _renderQuarterHeader(container) {
    const cursor = new Date(this.startDate);
    let currentQ = -1;
    let currentQYear = -1;
    let qStartOffset = 0;

    while (cursor <= this.endDate) {
      const q = Math.floor(cursor.getMonth() / 3);
      const y = cursor.getFullYear();
      if (q !== currentQ || y !== currentQYear) {
        if (currentQ !== -1) {
          this._addHeaderCell(container, qStartOffset, this._dayOffset(cursor) - qStartOffset, `Q${currentQ + 1} ${currentQYear}`);
        }
        currentQ = q;
        currentQYear = y;
        qStartOffset = this._dayOffset(cursor);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    if (currentQ !== -1) {
      this._addHeaderCell(container, qStartOffset, this.totalDays - qStartOffset, `Q${currentQ + 1} ${currentQYear}`);
    }
  }


  _addHeaderCell(container, startOffset, spanDays, label) {
    const dw = this.dayWidth;
    const width = spanDays * dw;
    if (width < 2) return;
    const el = document.createElement('div');
    el.style.cssText = `position:absolute;left:${startOffset * dw}px;top:0;width:${width}px;height:${HEADER_HEIGHT}px;display:flex;align-items:center;justify-content:center;padding:0 8px;font-size:12px;font-weight:600;color:#4B5563;border-right:1px solid #E5E7EB;overflow:hidden;user-select:none;`;
    if (width > 20) {
      el.textContent = label;
    }
    container.appendChild(el);
  }

  _renderBody(grid) {
    const dw = this.dayWidth;

    // Assign a color index per unique request, in top-to-bottom order
    const requestColorMap = {};
    let colorCounter = 0;
    (this.payload.aisles || []).forEach((aisle) => {
      if (!this.collapsedAisles.has(aisle.id)) {
        aisle.bays.forEach((bay) => {
          bay.requests.forEach((req) => {
            if (!(req.id in requestColorMap)) {
              requestColorMap[req.id] = colorCounter++;
            }
          });
        });
      }
    });

    (this.payload.aisles || []).forEach((aisle) => {
      // Aisle header row
      const aisleRow = document.createElement('div');
      aisleRow.style.cssText = `display:flex;height:${AISLE_HEADER_HEIGHT}px;`;

      const aisleLabel = document.createElement('div');
      aisleLabel.style.cssText = `position:sticky;left:0;z-index:15;width:${SIDEBAR_WIDTH}px;min-width:${SIDEBAR_WIDTH}px;height:${AISLE_HEADER_HEIGHT}px;background:#E5E7EB;border-bottom:1px solid #D1D5DB;border-right:1px solid #D1D5DB;display:flex;align-items:center;padding:0 8px;cursor:pointer;user-select:none;`;
      aisleLabel.dataset.action = 'click->forge-timeline#toggleAisle';
      aisleLabel.dataset.aisleId = aisle.id;

      const collapsed = this.collapsedAisles.has(aisle.id);
      aisleLabel.innerHTML = `<svg style="width:12px;height:12px;margin-right:4px;transform:${collapsed ? 'rotate(-90deg)' : ''};transition:transform 0.15s;flex-shrink:0;" fill="none" stroke="#6B7280" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path></svg><span style="font-size:12px;font-weight:600;color:#374151;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${this._escapeHtml(aisle.name)}</span>${aisle.edit_url ? `<a href="${this._escapeHtml(aisle.edit_url)}" class="link" style="font-size:11px;line-height:1;margin-left:6px;flex-shrink:0;" data-action="click->forge-timeline#stopPropagation">Edit</a>` : ''}`;

      const aisleBg = document.createElement('div');
      aisleBg.style.cssText = `position:relative;width:${this.timelineWidth}px;height:${AISLE_HEADER_HEIGHT}px;background:#E5E7EB;border-bottom:1px solid #D1D5DB;`;

      // Today line in aisle header
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayOff = this._dayOffset(today);
      if (todayOff >= 0 && todayOff <= this.totalDays) {
        const todayLine = document.createElement('div');
        todayLine.style.cssText = `position:absolute;left:${todayOff * dw}px;top:0;width:2px;height:${AISLE_HEADER_HEIGHT}px;background:#EF4444;z-index:5;pointer-events:none;`;
        aisleBg.appendChild(todayLine);
      }

      aisleRow.appendChild(aisleLabel);
      aisleRow.appendChild(aisleBg);
      grid.appendChild(aisleRow);

      if (!collapsed) {
        aisle.bays.forEach((bay) => {
          const bayRowCount = Math.max(bay.requests.length, 1);
          const bayHeight = bayRowCount * ROW_HEIGHT;

          const bayRow = document.createElement('div');
          bayRow.style.cssText = `display:flex;height:${bayHeight}px;`;

          // Sidebar bay label (sticky left)
          const bayLabel = document.createElement('div');
          bayLabel.style.cssText = `position:sticky;left:0;z-index:15;width:${SIDEBAR_WIDTH}px;min-width:${SIDEBAR_WIDTH}px;height:${bayHeight}px;background:#fff;border-bottom:1px solid #F3F4F6;border-right:1px solid #E5E7EB;display:flex;align-items:center;padding:0 8px 0 20px;font-size:12px;color:#4B5563;overflow:hidden;`;
          let bayText = bay.name;
          if (bay.size) bayText += ` (${bay.size})`;
          const auditLink = bay.latest_audit_url
            ? `<a href="${this._escapeHtml(bay.latest_audit_url)}" class="link" style="font-size:11px;line-height:1;margin-left:6px;flex-shrink:0;white-space:nowrap;" data-action="click->forge-timeline#stopPropagation">${this._escapeHtml(bay.latest_audit_date)}</a>`
            : '';
          bayLabel.innerHTML = `<span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${this._escapeHtml(bayText)}</span>${auditLink}`;

          // Timeline cell for this bay
          const bayTimeline = document.createElement('div');
          bayTimeline.style.cssText = `position:relative;width:${this.timelineWidth}px;height:${bayHeight}px;border-bottom:1px solid #F3F4F6;`;

          // Grid lines matching zoom boundaries
          this._getGridOffsets().forEach((lx) => {
            const line = document.createElement('div');
            line.style.cssText = `position:absolute;left:${lx}px;top:0;width:1px;height:${bayHeight}px;background:#E5E7EB;`;
            bayTimeline.appendChild(line);
          });

          // Today line in this row
          if (todayOff >= 0 && todayOff <= this.totalDays) {
            const todayLine = document.createElement('div');
            todayLine.style.cssText = `position:absolute;left:${todayOff * dw}px;top:0;width:2px;height:${bayHeight}px;background:#EF4444;z-index:5;pointer-events:none;`;
            bayTimeline.appendChild(todayLine);
          }

          // Request bars
          bay.requests.forEach((req, idx) => {
            this._renderBar(bayTimeline, req, idx * ROW_HEIGHT, aisle, bay, requestColorMap[req.id] || 0);
          });

          bayRow.appendChild(bayLabel);
          bayRow.appendChild(bayTimeline);
          grid.appendChild(bayRow);
        });
      }
    });
  }

  // Returns pixel offsets for vertical grid lines matching the current zoom level
  _getGridOffsets() {
    const dw = this.dayWidth;
    const offsets = [];
    const cursor = new Date(this.startDate);
    let prevKey = null;

    while (cursor <= this.endDate) {
      let key;
      if (this.currentZoom === 'year') {
        key = cursor.getFullYear();
      } else {
        key = `${cursor.getFullYear()}-${Math.floor(cursor.getMonth() / 3)}`;
      }
      if (key !== prevKey && prevKey !== null) {
        offsets.push(this._dayOffset(cursor) * dw);
      }
      prevKey = key;
      cursor.setDate(cursor.getDate() + 1);
    }
    return offsets;
  }

  _renderBar(container, req, y, aisle, bay, colorIndex) {
    if (!req.confirmed_start_date || !req.confirmed_end_date) return;

    const startDate = this._parseDate(req.confirmed_start_date);
    const endDate = this._parseDate(req.confirmed_end_date);
    const dw = this.dayWidth;

    const startOffset = this._dayOffset(startDate);
    const endOffset = this._dayOffset(endDate);
    const barWidth = Math.max((endOffset - startOffset + 1) * dw, 6);
    const x = startOffset * dw;

    const colors = BAR_COLORS[colorIndex % BAR_COLORS.length];

    const bar = document.createElement('div');
    bar.style.cssText = `position:absolute;left:${x}px;top:${y + 4}px;width:${barWidth}px;height:${ROW_HEIGHT - 8}px;background:${colors.bg};border:1px solid ${colors.border};color:${colors.text};border-radius:4px;cursor:pointer;z-index:10;transition:box-shadow 0.15s;overflow:hidden;`;

    if (barWidth > 50) {
      const label = document.createElement('span');
      label.style.cssText = `display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding:0 4px;font-size:11px;font-weight:500;line-height:${ROW_HEIGHT - 10}px;`;
      label.textContent = req.title;
      bar.appendChild(label);
    }

    bar.dataset.requestId = req.id;
    bar.dataset.action = 'click->forge-timeline#showDetail mouseenter->forge-timeline#highlightRelated mouseleave->forge-timeline#unhighlightRelated';
    bar.dataset.request = JSON.stringify(req);
    bar.dataset.aisleName = aisle.name;
    bar.dataset.bayName = bay.name;

    container.appendChild(bar);
  }

  // --- Actions ---

  zoom(event) {
    const level = event.currentTarget.dataset.zoom;
    if (!ZOOM_LEVELS[level]) return;

    const scroll = this.scrollContainerTarget;
    const oldTimelineW = this.timelineWidth;
    const scrollRatio = oldTimelineW > 0 ? scroll.scrollLeft / (SIDEBAR_WIDTH + oldTimelineW) : 0;

    this.currentZoom = level;

    this.zoomBtnTargets.forEach((btn) => {
      if (btn.dataset.zoom === level) {
        btn.className = 'px-3 py-1 text-sm rounded font-medium bg-blue-100 text-blue-700 focus:outline-none';
      } else {
        btn.className = 'px-3 py-1 text-sm rounded font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 focus:outline-none';
      }
    });

    this.render();

    scroll.scrollLeft = scrollRatio * (SIDEBAR_WIDTH + this.timelineWidth);
  }

  scrollToToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const offset = this._dayOffset(today);
    const scroll = this.scrollContainerTarget;
    const targetX = (offset * this.dayWidth) - (scroll.clientWidth / 2) + SIDEBAR_WIDTH;
    scroll.scrollLeft = Math.max(0, targetX);
  }

  toggleAisle(event) {
    const aisleId = parseInt(event.currentTarget.dataset.aisleId);
    if (this.collapsedAisles.has(aisleId)) {
      this.collapsedAisles.delete(aisleId);
    } else {
      this.collapsedAisles.add(aisleId);
    }
    this.render();
  }

  toggleUnscheduled() {
    this.unscheduledListTarget.classList.toggle('hidden');
    this.unscheduledChevronTarget.classList.toggle('rotate-180');
  }

  _renderUnscheduled() {
    const list = this.unscheduledListTarget;
    list.innerHTML = '';
    const items = this.payload.unscheduled || [];

    this.unscheduledCountTarget.textContent = items.length;

    if (items.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'text-sm text-gray-500 py-1';
      empty.textContent = 'No unscheduled requests.';
      list.appendChild(empty);
      return;
    }

    items.forEach((req) => {
      const row = document.createElement('div');
      row.className = 'flex items-center justify-between py-1 cursor-pointer hover:bg-gray-50 rounded px-1 -mx-1';
      row.dataset.action = 'click->forge-timeline#showDetail';
      row.dataset.request = JSON.stringify(req);

      const info = document.createElement('div');
      const title = document.createElement('span');
      title.className = 'text-sm font-medium text-blue-600 hover:text-blue-800';
      title.textContent = req.title;
      const dept = document.createElement('span');
      dept.className = 'text-xs text-gray-500 ml-2';
      dept.textContent = req.requester_dept || '';

      info.appendChild(title);
      info.appendChild(dept);
      row.appendChild(info);
      list.appendChild(row);
    });
  }

  showDetail(event) {
    const bar = event.currentTarget;
    const req = JSON.parse(bar.dataset.request);

    // Set form action
    this.panelFormTarget.action = req.update_url;

    // Static text fields
    this.panelTitleTarget.textContent = req.title || 'Untitled';
    this.panelRequesterTarget.textContent = req.requester_name || '-';
    this.panelDeptTarget.textContent = req.requester_dept || '-';
    this.panelDescriptionTarget.textContent = req.description || '-';

    // Editable form fields
    this.panelStatusTarget.value = req.status || '';
    this.panelStartTarget.value = req.confirmed_start_date || '';
    this.panelEndTarget.value = req.confirmed_end_date || '';
    this.panelLinkTarget.href = req.show_url;

    // Build bay checkboxes
    this._currentRequestId = req.id;
    this._buildBayCheckboxes(req.id);

    this.sidePanelTarget.classList.remove('translate-x-full');
    this.panelBackdropTarget.classList.remove('hidden');
  }

  // Build the bay assignment checkboxes grouped by aisle
  _buildBayCheckboxes(requestId) {
    const container = this.panelBaysContainerTarget;
    container.innerHTML = '';

    // Collect all existing assignments for this request: { bay_id -> calendar_id }
    const assignments = {};
    (this.payload.aisles || []).forEach((aisle) => {
      aisle.bays.forEach((bay) => {
        bay.requests.forEach((r) => {
          if (r.id === requestId && r.calendar_id) {
            assignments[bay.id] = r.calendar_id;
          }
        });
      });
    });

    this._bayAssignments = assignments;

    (this.payload.aisles || []).forEach((aisle) => {
      // Aisle label
      const aisleLabel = document.createElement('div');
      aisleLabel.className = 'text-xs font-semibold text-gray-500 uppercase mt-2 first:mt-0';
      aisleLabel.textContent = aisle.name;
      container.appendChild(aisleLabel);

      aisle.bays.forEach((bay) => {
        const isAssigned = bay.id in assignments;

        const label = document.createElement('label');
        label.className = 'flex items-center space-x-2 py-0.5 cursor-pointer';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'form-checkbox h-4 w-4 text-blue-600 rounded';
        checkbox.checked = isAssigned;
        checkbox.dataset.bayId = bay.id;
        checkbox.dataset.calendarId = isAssigned ? assignments[bay.id] : '';
        checkbox.addEventListener('change', () => this._buildBayInputs());

        const text = document.createElement('span');
        text.className = 'text-sm text-gray-700';
        text.textContent = bay.name + (bay.size ? ` (${bay.size})` : '');

        label.appendChild(checkbox);
        label.appendChild(text);
        container.appendChild(label);
      });
    });

    this._buildBayInputs();
  }

  // Rebuild hidden inputs for forge_calendars_attributes based on checkbox state
  _buildBayInputs() {
    // Remove existing calendar hidden inputs
    this.panelFormTarget.querySelectorAll('[data-bay-input]').forEach((el) => el.remove());

    const container = this.panelBaysContainerTarget;
    const checkboxes = container.querySelectorAll('input[type="checkbox"]');
    let idx = 0;

    checkboxes.forEach((cb) => {
      const bayId = cb.dataset.bayId;
      const calendarId = cb.dataset.calendarId;
      const prefix = `forge_request[forge_calendars_attributes][${idx}]`;

      if (cb.checked && calendarId) {
        // Existing assignment, keep it
        this._addHiddenInput(prefix + '[id]', calendarId);
        this._addHiddenInput(prefix + '[forge_aisle_bay_id]', bayId);
        idx++;
      } else if (cb.checked && !calendarId) {
        // New assignment
        this._addHiddenInput(prefix + '[forge_aisle_bay_id]', bayId);
        idx++;
      } else if (!cb.checked && calendarId) {
        // Removed assignment
        this._addHiddenInput(prefix + '[id]', calendarId);
        this._addHiddenInput(prefix + '[_destroy]', '1');
        idx++;
      }
      // Unchecked with no calendarId: skip
    });
  }

  _addHiddenInput(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.dataset.bayInput = 'true';
    this.panelFormTarget.appendChild(input);
  }

  hideDetail() {
    this.sidePanelTarget.classList.add('translate-x-full');
    this.panelBackdropTarget.classList.add('hidden');
  }

  highlightRelated(event) {
    const requestId = event.currentTarget.dataset.requestId;
    this.gridContainerTarget.querySelectorAll('[data-request-id]').forEach((el) => {
      if (el.dataset.requestId === requestId) {
        el.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.5), 0 4px 6px rgba(0,0,0,0.1)';
        el.style.zIndex = '30';
      } else {
        el.style.opacity = '0.4';
      }
    });
  }

  unhighlightRelated() {
    this.gridContainerTarget.querySelectorAll('[data-request-id]').forEach((el) => {
      el.style.boxShadow = '';
      el.style.opacity = '';
      el.style.zIndex = '';
    });
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  _escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}
